import { Button, Form, Input, Modal, Popconfirm, Popover, Tag, Typography, Upload, UploadProps } from 'antd'
import React from 'react'
import moment from 'moment'
import { DeleteFilled, InboxOutlined, UndoOutlined } from '@ant-design/icons'
import { ColumnsType } from 'antd/es/table'
import { scrollBannersApi } from 'api/scrollBanners'
import { EndDateCell, EndDateField, FixedPositionFields, reorderRows, SortableTable } from 'components/ui'
import { EScrollBannerStatus, IScrollBanner } from 'models/scrollBanners'
import { LOCKED_ROW_HINT } from 'shared/constants/rotation'
import { IMAGES_URL } from 'shared/constants/urls'
import { fromEndDateField, toEndDateField } from 'shared/utils/endDate'
import { formatAppScreen, getAppScreenFromUrl } from 'shared/utils/bannerAppScreen'
import { normalizeFile } from 'shared/utils/normalizeFile'
import { showMessage } from 'shared/utils/showMessage'
import { showServerError } from 'shared/utils/showServerError'
import { HeaderList } from '../Banners/components/HeaderList'

const PHOTO_MIME_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp']

const photoUploadProps: UploadProps = {
  maxCount: 1,
  listType: 'picture',
  accept: 'image/png,image/jpeg,image/jpg,image/webp',
  beforeUpload: (file) => {
    if (!PHOTO_MIME_TYPES.includes(file.type)) {
      showMessage('error', 'Поддерживаются только форматы JPG, JPEG, PNG, WebP', '')
      return Upload.LIST_IGNORE
    }
    return true
  },
  customRequest: ({ onSuccess }) => onSuccess?.('ok')
}

// текущее фото — уже загруженный элемент, повторно загружать не нужно
const getPhotoFileList = (photo?: string) =>
  photo ? [{ uid: '-1', name: 'Текущее фото', status: 'done', url: `${IMAGES_URL}${photo}` }] : []

const getFormValues = (banner: IScrollBanner | null) => ({
  end_date: toEndDateField(banner?.end_date),
  fixed_position: banner?.fixed_position ?? false,
  webUrl: banner?.webUrl ?? undefined,
  photo_desktop: getPhotoFileList(banner?.photo_desktop),
  photo: getPhotoFileList(banner?.photo)
})

const Rules = () => (
  <Typography.Paragraph type='secondary' style={{ maxWidth: 760, marginBottom: 0 }}>
    Баннеры показываются на сайте и в приложении между карточками товаров — на главной, в поиске, в категориях и на странице магазина — и по очереди сменяют друг друга.
  </Typography.Paragraph>
)

// Как баннер стоит в ленте: на ПК — ряд сетки при наибольшей ширине сайта (1440 минус поля
// секции по 23), в приложении — ряд на телефоне шириной 390 (минус поля по 15)
const REAL_SIZE = {
  desktop: { width: 1394, ratio: '8 / 1', radius: 15 },
  app: { width: 360, ratio: '4 / 1', radius: 13 }
}

interface IPreviewProps {
  photo: string
  width: number
  kind: keyof typeof REAL_SIZE
}

const Preview: React.FC<IPreviewProps> = ({ photo, width, kind }) => (
  // у bottom antd сдвигает окно внутрь экрана, у bottomLeft — нет
  <Popover
    placement='bottom'
    mouseEnterDelay={0.2}
    content={
      <img
        src={`${IMAGES_URL}${photo}`}
        alt=''
        style={{
          display: 'block',
          width: REAL_SIZE[kind].width,
          // окно меньше реального размера — ужимаем, чтобы не выйти за край страницы
          maxWidth: 'calc(100vw - 80px)',
          // пропорции и обрезка как в ленте, а не как у файла
          aspectRatio: REAL_SIZE[kind].ratio,
          objectFit: 'cover',
          borderRadius: REAL_SIZE[kind].radius
        }}
      />
    }
  >
    <div
      style={{
        width,
        height: 20,
        backgroundImage: `url(${IMAGES_URL}${photo})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        borderRadius: 4,
        cursor: 'zoom-in'
      }}
    />
  </Popover>
)

export const ScrollBannerList: React.FC = () => {
  const [isLoading, setIsLoading] = React.useState(false)
  const [banners, setBanners] = React.useState<IScrollBanner[]>([])
  const [showArchived, setShowArchived] = React.useState(false)
  const [isModalOpen, setIsModalOpen] = React.useState(false)
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  // null — режим создания, иначе редактируем этот баннер
  const [editingBanner, setEditingBanner] = React.useState<IScrollBanner | null>(null)
  const [form] = Form.useForm()
  const webUrlScreen = getAppScreenFromUrl(Form.useWatch('webUrl', form))

  const getBanners = async () => {
    try {
      setIsLoading(true)
      const response = await scrollBannersApi.getScrollBanners()
      setBanners(response.data)
    } catch (e) {
      showServerError(e)
    } finally {
      setIsLoading(false)
    }
  }

  React.useEffect(() => {
    getBanners()
  }, [])

  // список приходит в порядке показа; закреплённые не перетаскиваются
  const activeBanners = banners.filter((banner) => banner.status !== EScrollBannerStatus.ARCHIVED)
  const sortableIds = activeBanners.filter((banner) => !banner.fixed_position).map((banner) => banner._id)
  const lockedIds = activeBanners.filter((banner) => banner.fixed_position).map((banner) => banner._id)

  const handleSortEnd = async (ids: string[]) => {
    setBanners((prev) => reorderRows(prev, ids))
    try {
      await scrollBannersApi.reorderScrollBanners(ids)
    } catch (e) {
      showServerError(e)
      await getBanners()
    }
  }

  // инстанс формы переживает закрытие модалки — заполняем руками при каждом открытии
  React.useEffect(() => {
    if (isModalOpen) form.setFieldsValue(getFormValues(editingBanner))
  }, [isModalOpen, editingBanner])

  const runAndReload = async (action: () => Promise<unknown>) => {
    try {
      await action()
      await getBanners()
    } catch (e) {
      showServerError(e)
    }
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setEditingBanner(null)
    form.resetFields()
  }

  const openModal = (banner: IScrollBanner | null) => {
    setEditingBanner(banner)
    setIsModalOpen(true)
  }

  const handleSubmit = async (values: any) => {
    const payload = {
      fixed_position: !!values.fixed_position,
      end_date: fromEndDateField(values.end_date),
      webUrl: values.webUrl ?? '',
      // файл передаём только новый — иначе остаётся прежний
      photo: values.photo?.[0]?.originFileObj,
      photo_desktop: values.photo_desktop?.[0]?.originFileObj
    }
    try {
      setIsSubmitting(true)
      if (editingBanner) {
        await scrollBannersApi.updateScrollBanner(editingBanner._id, payload)
      } else {
        await scrollBannersApi.createScrollBanner(payload)
      }
      closeModal()
      await getBanners()
    } catch (e) {
      showServerError(e)
    } finally {
      setIsSubmitting(false)
    }
  }

  const displayedBanners = showArchived
    ? banners
    : banners.filter((banner) => banner.status !== EScrollBannerStatus.ARCHIVED)

  const columns: ColumnsType<IScrollBanner> = [
    {
      title: (
        <>
          8:1
          <br />
          Сайт
        </>
      ),
      dataIndex: 'photo_desktop',
      width: 180,
      render: (_, { photo_desktop }) => <Preview photo={photo_desktop} width={160} kind='desktop' />
    },
    {
      title: (
        <>
          4:1
          <br />
          Приложение
        </>
      ),
      dataIndex: 'photo',
      width: 140,
      render: (_, { photo }) => <Preview photo={photo} width={80} kind='app' />
    },
    {
      title: 'Ссылка / Экран',
      key: 'link',
      // длинный адрес переносится, и колонка уступает ширину остальным
      onCell: () => ({ style: { overflowWrap: 'anywhere' } }),
      render: (_, { webUrl, navigation_path, navigation_params }) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span>
            <strong>Ссылка</strong>: {webUrl || '—'}
          </span>
          <span>
            <strong>Экран</strong>: {formatAppScreen(navigation_path, navigation_params) || '—'}
          </span>
        </div>
      )
    },
    {
      title: 'Дата создания',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 140,
      render: (_, { createdAt }) => (createdAt ? moment(createdAt).format('DD.MM.YYYY') : '—')
    },
    {
      title: 'Дата окончания',
      dataIndex: 'end_date',
      key: 'end_date',
      width: 150,
      render: (_, { end_date }) => <EndDateCell value={end_date} />
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      width: 120,
      render: (_, { status }) => (
        <Tag color={status === EScrollBannerStatus.PUBLISHED ? 'success' : 'default'}>
          {status === EScrollBannerStatus.PUBLISHED ? 'Активен' : 'Архив'}
        </Tag>
      )
    },
    {
      title: '',
      dataIndex: 'operation',
      width: 100,
      // клик по кнопкам и их подтверждениям не должен открывать баннер, как клик по строке
      render: (_, banner) => (
        <div style={{ display: 'flex', gap: 8 }} onClick={(e) => e.stopPropagation()}>
          {banner.status === EScrollBannerStatus.ARCHIVED ? (
            <>
              <Button
                icon={<UndoOutlined />}
                size='small'
                onClick={() =>
                  runAndReload(() =>
                    scrollBannersApi.updateScrollBannerStatus(banner._id, EScrollBannerStatus.PUBLISHED)
                  )
                }
              />
              <Popconfirm
                title='Удалить баннер безвозвратно?'
                onConfirm={() => runAndReload(() => scrollBannersApi.deleteScrollBanner(banner._id))}
              >
                <Button type='primary' danger icon={<DeleteFilled />} size='small' />
              </Popconfirm>
            </>
          ) : (
            <Popconfirm
              title='Отправить в архив?'
              onConfirm={() =>
                runAndReload(() =>
                  scrollBannersApi.updateScrollBannerStatus(banner._id, EScrollBannerStatus.ARCHIVED)
                )
              }
            >
              <Button type='primary' danger icon={<InboxOutlined />} size='small' />
            </Popconfirm>
          )}
        </div>
      )
    }
  ]

  return (
    <>
      <div style={{ paddingBottom: 80 }}>
        <HeaderList
          onClick={() => openModal(null)}
          showArchived={showArchived}
          onShowArchivedChange={setShowArchived}
          description={<Rules />}
        />
        <SortableTable
          scroll={{ x: 850 }}
          pagination={false}
          columns={columns}
          dataSource={displayedBanners}
          loading={isLoading}
          rowClassName='tableRow'
          onRow={(banner) => ({ onClick: () => openModal(banner) })}
          sortableIds={sortableIds}
          lockedIds={lockedIds}
          lockedHint={LOCKED_ROW_HINT}
          onSortEnd={handleSortEnd}
        />
      </div>

      <Modal
        title={editingBanner ? 'Редактировать скролл-баннер' : 'Добавить скролл-баннер'}
        open={isModalOpen}
        onCancel={closeModal}
        footer={null}
        destroyOnClose
      >
        <Form form={form} layout='vertical' onFinish={handleSubmit} initialValues={getFormValues(null)}>
          <Form.Item
            label='Фото для ПК-версии сайта'
            name='photo_desktop'
            valuePropName='fileList'
            getValueFromEvent={normalizeFile}
            rules={[{ required: true, message: 'Добавьте фото для ПК' }]}
            extra='Соотношение сторон 8:1, например 1920×240. JPG, PNG или WebP.'
          >
            <Upload {...photoUploadProps}>
              <Button>Загрузить фото</Button>
            </Upload>
          </Form.Item>

          <Form.Item
            label='Фото для приложения и мобильного веба'
            name='photo'
            valuePropName='fileList'
            getValueFromEvent={normalizeFile}
            rules={[{ required: true, message: 'Добавьте фото для телефона' }]}
            extra='Соотношение сторон 4:1, например 1200×300. JPG, PNG или WebP.'
          >
            <Upload {...photoUploadProps}>
              <Button>Загрузить фото</Button>
            </Upload>
          </Form.Item>

          <FixedPositionFields />

          <EndDateField subject='баннер' />

          <Form.Item
            label='URL (подборка / продавец / товар)'
            name='webUrl'
            rules={[{ required: true, message: 'Укажите URL' }]}
          >
            <Input placeholder='https://horsesmart.store/...' />
          </Form.Item>

          <Form.Item
            label='Экран в приложении'
            extra='Определяется по ссылке: в приложении баннер ведёт туда же, куда на сайте.'
          >
            <Input
              disabled
              value={formatAppScreen(webUrlScreen?.navigation_path, webUrlScreen?.navigation_params)}
              placeholder='У этой ссылки нет экрана в приложении — приложение откроет её в браузере'
            />
          </Form.Item>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
            <Button onClick={closeModal}>Отмена</Button>
            <Button type='primary' htmlType='submit' loading={isSubmitting}>
              {editingBanner ? 'Сохранить' : 'Создать'}
            </Button>
          </div>
        </Form>
      </Modal>
    </>
  )
}
